import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  MY_STATES,
  SHIPPING_COUNTRIES,
  evaluateShippingFee,
  mergeCountryRates,
  mergeStateRates,
  parseShippingAdvancedConfig,
  type ShippingFeeBreakdown,
  type ShippingLineInput,
} from "@gaole/shared";
import { api } from "../../lib/api";
import { rm } from "../../lib/format";
import { Button, Input, SearchSelect, SettingsCard } from "../../components/ui";

type SimulatorLine = {
  key: string;
  productType: string;
  quantity: string;
  weightGrams: string;
  productId?: string;
};

function newLine(): SimulatorLine {
  return {
    key: `line-${Math.random().toString(36).slice(2, 9)}`,
    productType: "SINGLE",
    quantity: "1",
    weightGrams: "100",
  };
}

function simulatorLineInput(line: SimulatorLine): ShippingLineInput {
  return {
    productType: line.productType,
    quantity: Number.parseInt(line.quantity, 10) || 1,
    weightGrams: Number.parseInt(line.weightGrams, 10) || 0,
  };
}

function BreakdownView({ breakdown }: { breakdown: ShippingFeeBreakdown }) {
  return (
    <div className="rounded-xl border border-line bg-surface-2 p-4 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-muted">Base fee</span>
        <span className="font-medium tabular-nums">{rm(breakdown.baseFee)}</span>
      </div>
      {breakdown.freeShippingApplied && (
        <p className="mt-2 text-xs text-emerald-300">Free shipping threshold applied — total is RM 0.00</p>
      )}
      {breakdown.surcharges.map((row) => (
        <div key={row.ruleId} className="mt-2 flex items-center justify-between text-xs">
          <span className="text-foreground">{row.label}</span>
          <span className="font-medium tabular-nums text-brand-pink">+ {rm(row.fee)}</span>
        </div>
      ))}
      <div className="mt-3 flex items-center justify-between border-t border-line pt-3 font-display font-semibold">
        <span>Total shipping</span>
        <span className="tabular-nums text-brand-pink">{rm(breakdown.totalFee)}</span>
      </div>
    </div>
  );
}

export function ShippingSimulator({
  draft,
  productTypeOptions,
  productOptions,
}: {
  draft: Record<string, unknown>;
  productTypeOptions: { value: string; label: string }[];
  productOptions: { value: string; label: string; hint?: string; weightGrams?: number; productType?: string }[];
}) {
  const [country, setCountry] = useState("MY");
  const [state, setState] = useState<string>(MY_STATES[11]?.label ?? "Selangor");
  const [subtotal, setSubtotal] = useState("100");
  const [lines, setLines] = useState<SimulatorLine[]>([newLine()]);
  const [localBreakdown, setLocalBreakdown] = useState<ShippingFeeBreakdown | null>(null);
  const [serverBreakdown, setServerBreakdown] = useState<ShippingFeeBreakdown | null>(null);

  const settingsInput = useMemo(() => {
    const fallbackFee = Number(draft["shipping.flat_fee"] ?? 10);
    return {
      fallbackFee,
      freeAbove: Number(draft["shipping.free_above"] ?? 0),
      stateRates: mergeStateRates(draft["shipping.state_rates"], fallbackFee),
      countryRates: mergeCountryRates(draft["shipping.country_rates"]),
      advanced: parseShippingAdvancedConfig(draft["shipping.advanced"]),
    };
  }, [draft]);

  const simulateMut = useMutation({
    mutationFn: () =>
      api<{ breakdown: ShippingFeeBreakdown }>("/settings/shipping/simulate", {
        method: "POST",
        body: {
          country,
          state: country === "MY" ? state : null,
          subtotal: Number.parseFloat(subtotal) || 0,
          lines: lines.map(simulatorLineInput),
        },
      }),
    onSuccess: (res) => setServerBreakdown(res.breakdown),
  });

  function calculateLocal() {
    const breakdown = evaluateShippingFee({
      ...settingsInput,
      country,
      state: country === "MY" ? state : null,
      subtotal: Number.parseFloat(subtotal) || 0,
      lines: lines.map(simulatorLineInput),
    });
    setLocalBreakdown(breakdown);
    setServerBreakdown(null);
  }

  return (
    <SettingsCard
      title="Fee simulator"
      description="Preview shipping from your current draft settings before saving."
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <SearchSelect
          label="Country"
          options={SHIPPING_COUNTRIES.map((row) => ({ value: row.code, label: row.label }))}
          value={country}
          onChange={setCountry}
        />
        {country === "MY" ? (
          <SearchSelect
            label="State"
            options={MY_STATES.map((row) => ({ value: row.label, label: row.label }))}
            value={state}
            onChange={setState}
          />
        ) : (
          <div />
        )}
        <Input
          label="Merchandise subtotal (RM)"
          type="number"
          step="0.01"
          min="0"
          value={subtotal}
          onChange={(e) => setSubtotal(e.target.value)}
        />
      </div>

      <div className="mt-4 space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-2">Cart lines</p>
        {lines.map((line, index) => (
          <div key={line.key} className="grid gap-2 rounded-xl border border-line bg-surface-2 p-3 sm:grid-cols-4">
            <SearchSelect
              label="Product (optional)"
              placeholder="Pick a product…"
              options={[{ value: "", label: "Manual line" }, ...productOptions]}
              value={line.productId ?? ""}
              onChange={(productId) => {
                const next = [...lines];
                if (!productId) {
                  next[index] = { ...line, productId: undefined };
                  setLines(next);
                  return;
                }
                const product = productOptions.find((p) => p.value === productId);
                if (!product) return;
                next[index] = {
                  ...line,
                  productId,
                  weightGrams: String(product.weightGrams ?? line.weightGrams),
                  productType: product.productType ?? line.productType,
                };
                setLines(next);
              }}
            />
            <SearchSelect
              label="Product type"
              options={productTypeOptions}
              value={line.productType}
              onChange={(productType) => {
                const next = [...lines];
                next[index] = { ...line, productType };
                setLines(next);
              }}
            />
            <Input
              label="Qty"
              type="number"
              min="1"
              value={line.quantity}
              onChange={(e) => {
                const next = [...lines];
                next[index] = { ...line, quantity: e.target.value };
                setLines(next);
              }}
            />
            <Input
              label="Weight (g)"
              type="number"
              min="0"
              value={line.weightGrams}
              onChange={(e) => {
                const next = [...lines];
                next[index] = { ...line, weightGrams: e.target.value };
                setLines(next);
              }}
            />
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setLines((rows) => [...rows, newLine()])}>
            Add line
          </Button>
          {lines.length > 1 && (
            <Button size="sm" variant="ghost" onClick={() => setLines((rows) => rows.slice(0, -1))}>
              Remove last
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm" onClick={calculateLocal}>
          Calculate (draft)
        </Button>
        <Button size="sm" variant="outline" disabled={simulateMut.isPending} onClick={() => simulateMut.mutate()}>
          Verify on server
        </Button>
      </div>

      {localBreakdown && (
        <div className="mt-4 space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-2">Draft preview</p>
          <BreakdownView breakdown={localBreakdown} />
        </div>
      )}

      {serverBreakdown && (
        <div className="mt-4 space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-2">Saved settings</p>
          <BreakdownView breakdown={serverBreakdown} />
        </div>
      )}
    </SettingsCard>
  );
}
